'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RealtimeMonitoringProps {
  currentLanguage: string;
  activeScope: 'scope1' | 'scope2' | 'scope3';
  onScopeChange: (scope: 'scope1' | 'scope2' | 'scope3') => void;
}

const RealtimeMonitoring = ({ currentLanguage, activeScope, onScopeChange }: RealtimeMonitoringProps) => {
  const [emissionData, setEmissionData] = useState({
    scope1: 2847,
    scope2: 4521,
    scope3: 12658,
  });

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setEmissionData((prev) => ({
        scope1: prev.scope1 + Math.floor(Math.random() * 10 - 5),
        scope2: prev.scope2 + Math.floor(Math.random() * 15 - 7),
        scope3: prev.scope3 + Math.floor(Math.random() * 25 - 12),
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const scopes = [
    {
      id: 'scope1',
      name: 'Scope 1',
      description: 'Direct emissions from owned sources',
      icon: 'FireIcon',
      color: 'error',
      value: emissionData.scope1,
      change: -2.3,
      sources: ['Facility boilers', 'Company vehicles', 'Manufacturing processes', 'Fugitive emissions'],
    },
    {
      id: 'scope2',
      name: 'Scope 2',
      description: 'Indirect emissions from purchased energy',
      icon: 'BoltIcon',
      color: 'warning',
      value: emissionData.scope2,
      change: -1.8,
      sources: ['Purchased electricity', 'Purchased steam', 'Purchased heating', 'Purchased cooling'],
    },
    {
      id: 'scope3',
      name: 'Scope 3',
      description: 'Other indirect emissions in value chain',
      icon: 'TruckIcon',
      color: 'info',
      value: emissionData.scope3,
      change: +0.5,
      sources: ['Business travel', 'Employee commuting', 'Waste disposal', 'Supply chain emissions'],
    },
  ];

  const selectedScope = scopes.find((s) => s.id === activeScope) || scopes[0];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-4">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span className="text-sm font-semibold text-success">Live Monitoring</span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Real-Time Emission Dashboard
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Monitor emissions across all scopes with AI-powered analytics and predictive insights
          </p>
        </div>

        {/* Scope Selector */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {scopes.map((scope) => (
            <button
              key={scope.id}
              onClick={() => onScopeChange(scope.id as any)}
              className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                activeScope === scope.id
                  ? `border-${scope.color} bg-${scope.color}/10 shadow-natural-lg`
                  : 'border-border bg-card hover:border-primary/50'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 bg-${scope.color}/10 rounded-lg`}>
                  <Icon name={scope.icon} size={28} className={`text-${scope.color}`} />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-heading font-bold text-text-primary">
                    {scope.value.toLocaleString()}
                  </div>
                  <div className="text-xs text-text-secondary">tCO₂e</div>
                </div>
              </div>

              <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{scope.name}</h3>
              <p className="text-sm text-text-secondary mb-3">{scope.description}</p>

              <div className="flex items-center justify-between">
                <span
                  className={`text-sm font-semibold ${
                    scope.change < 0 ? 'text-success' : 'text-error'
                  }`}
                >
                  {scope.change > 0 ? '+' : ''}
                  {scope.change}% vs last month
                </span>
                <Icon
                  name={scope.change < 0 ? 'ArrowDownIcon' : 'ArrowUpIcon'}
                  size={16}
                  className={scope.change < 0 ? 'text-success' : 'text-error'}
                />
              </div>
            </button>
          ))}
        </div>

        {/* Detailed View */}
        <div className="bg-card border-2 border-primary rounded-2xl p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
                {selectedScope.name} Detailed Analysis
              </h3>
              <p className="text-text-secondary">{selectedScope.description}</p>
            </div>
            <div className="text-right">
              <div className={`text-5xl font-heading font-bold text-${selectedScope.color}`}>
                {selectedScope.value.toLocaleString()}
              </div>
              <div className="text-sm text-text-secondary">tCO₂e Current Month</div>
            </div>
          </div>

          {/* Emission Sources */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {selectedScope.sources.map((source, index) => (
              <div key={index} className="bg-surface border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-text-primary">{source}</h4>
                  <Icon name="ChartBarIcon" size={20} className="text-primary" />
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-2xl font-heading font-bold text-text-primary">
                    {Math.floor(selectedScope.value / selectedScope.sources.length).toLocaleString()}
                  </span>
                  <span className="text-xs text-text-secondary">tCO₂e</span>
                </div>
                <div className="mt-3 w-full bg-border rounded-full h-2 overflow-hidden">
                  <div
                    className={`bg-${selectedScope.color} h-full rounded-full transition-all duration-700`}
                    style={{ width: `${Math.random() * 40 + 50}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* AI Insights */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <Icon name="SparklesIcon" size={24} className="text-primary mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-primary mb-2">AI-Powered Insight</h4>
                <p className="text-sm text-text-primary mb-3">
                  Detected {Math.floor(Math.random() * 5 + 3)} optimization opportunities that could reduce {selectedScope.name} emissions by {Math.floor(Math.random() * 10 + 5)}% over the next quarter.
                </p>
                <button className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
                  View Recommendations →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RealtimeMonitoring;